import { useEffect, useMemo, useRef, useState } from 'react';
import './EnderClubLoader.scss';

const WORD = 'ENDERCLUB';
const EXIT_MS = 750;

export interface PreloaderProps {
    /** Valore di caricamento corrente (0 → `max`). Può saltare bruscamente: la barra lo insegue in modo fluido. */
    value: number;
    /** Valore che corrisponde al 100%. Default: 100. */
    max?: number;
    /** Velocità massima della barra, in % al secondo. Default: 55 (≈ 1.8s per 0→100). */
    maxSpeed?: number;
    /** 0–1, rapidità di inseguimento del target. Più basso = più morbido. Default: 0.08. */
    smoothing?: number;
    /** Chiamato quando la barra raggiunge il 100%. */
    onComplete?: () => void;
    /** Chiamato al termine dell'animazione di uscita. */
    onExited?: () => void;
}

export default function EnderClubLoader({
                                      value,
                                      max = 100,
                                      maxSpeed = 55,
                                      smoothing = 0.08,
                                      onComplete,
                                      onExited,
                                  }: PreloaderProps) {
    const [progress, setProgress] = useState(0); // 0..1 → valore *visivo*
    const [leaving, setLeaving] = useState(false);

    /* ----------------------------------------------------------------
     * Callback sempre aggiornate senza riavviare il loop di animazione
     * ---------------------------------------------------------------- */
    const onCompleteRef = useRef(onComplete);
    const onExitedRef = useRef(onExited);
    useEffect(() => {
        onCompleteRef.current = onComplete;
        onExitedRef.current = onExited;
    });

    /* ----------------------------------------------------------------
     * Target normalizzato 0..1
     * ---------------------------------------------------------------- */
    const target = useMemo(() => {
        if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
        return Math.min(1, Math.max(0, value / max));
    }, [value, max]);

    const targetRef = useRef(target);
    const smoothingRef = useRef(smoothing);
    const maxSpeedRef = useRef(maxSpeed);

    useEffect(() => {
        targetRef.current = target;
    }, [target]);

    useEffect(() => {
        smoothingRef.current = smoothing;
        maxSpeedRef.current = maxSpeed;
    }, [smoothing, maxSpeed]);

    /* ----------------------------------------------------------------
     * Loop di animazione: insegue il target in modo fluido
     * ---------------------------------------------------------------- */
    useEffect(() => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduceMotion) {
            setProgress(1);
            onCompleteRef.current?.();
            const t = window.setTimeout(() => onExitedRef.current?.(), 60);
            return () => window.clearTimeout(t);
        }

        let raf = 0;
        let exitTimer = 0;
        let last = performance.now();
        let current = 0;
        let finished = false;

        const tick = (now: number) => {
            // dt limitato: evita salti enormi se il tab va in background
            const dt = Math.min(64, Math.max(0, now - last));
            last = now;

            const goal = targetRef.current;

            // 1) inseguimento esponenziale, indipendente dal frame-rate
            const k = 1 - Math.pow(1 - smoothingRef.current, dt / (1000 / 60));
            let next = current + (goal - current) * k;

            // 2) tetto di velocità → anche su un salto 20 → 70 la barra
            //    si muove lentamente e in modo costante
            const maxStep = (maxSpeedRef.current / 100) * (dt / 1000);
            if (Math.abs(next - current) > maxStep) {
                next = current + Math.sign(next - current) * maxStep;
            }

            // 3) aggancia il target quando è vicinissimo (evita code infinite)
            if (Math.abs(goal - next) < 0.001) next = goal;

            current = next;
            setProgress(next);

            // 4) completato solo quando l'animazione ha davvero raggiunto il 100%
            if (!finished && goal >= 1 && next >= 0.9999) {
                finished = true;
                onCompleteRef.current?.();
                setLeaving(true);
                exitTimer = window.setTimeout(() => onExitedRef.current?.(), EXIT_MS);
            }

            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(raf);
            window.clearTimeout(exitTimer);
        };
    }, []);

    const percent = Math.round(progress * 100);

    return (
        <div
            role="status"
            aria-label="Caricamento EnderClub"
            className={`preloader${leaving ? ' preloader--leaving' : ''}`}
        >
            {/* Logo */}
            <div className="preloader__logo">
                <div className="preloader__logo-glow" aria-hidden="true" />
                <img
                    className="preloader__logo-img"
                    src="https://ik.imagekit.io/n4dmneisyc/enderclub/logo.jpg"
                    alt=""
                />
            </div>

            {/* Wordmark animato */}
            <div className="preloader__wordmark" aria-hidden="true">
                {WORD.split('').map((ch, i) => (
                    <span
                        key={i}
                        className="preloader__letter"
                        style={{ animationDelay: `${0.2 + i * 0.06}s` }}
                    >
            {ch}
          </span>
                ))}
            </div>

            {/* Barra di progresso */}
            <div
                className="preloader__bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
            >
                <div className="preloader__bar-fill" style={{ width: `${percent}%` }} />
            </div>

            <p className="preloader__tagline">Social Media Agency</p>
        </div>
    );
}